import { area, kinks, point as turfPoint, explode, nearestPoint, distance, lineIntersect, nearestPointOnLine, polygonToLine, booleanPointInPolygon } from '@turf/turf';

/**
 * Calculates the area of a polygon in hectares.
 * Uses Turf.js for accurate geodesic area calculation.
 * @param {Object} geojson - GeoJSON Polygon object
 * @returns {number} Area in hectares, rounded to 2 decimal places
 */
export const calculateAreaHectares = (geojson) => {
  if (!geojson) return 0;
  const areaSqMeters = area(geojson);
  const areaHectares = areaSqMeters / 10000;
  return Math.round(areaHectares * 100) / 100;
};

/**
 * Validates a polygon to ensure it's not self-intersecting.
 * @param {Object} geojson - GeoJSON Polygon object
 * @returns {boolean} True if valid
 */
export const validatePolygon = (geojson) => {
  if (!geojson) return false;
  // Turf.kinks returns points of self-intersection
  const result = kinks(geojson);
  return result.features.length === 0;
};

/**
 * Snaps a point to the nearest vertex of existing polygons if within threshold.
 * @param {Array} point - [lng, lat]
 * @param {Array} features - Array of GeoJSON features to snap to
 * @param {number} thresholdKm - Distance threshold in km (default 0.00001 ~1m)
 * @returns {Array} Snapped point [lng, lat] or original point
 */
export const snapToVertex = (point, features, thresholdKm = 0.00001) => {
  let nearestVertex = null;
  let minDistance = Infinity;

  const targetPoint = turfPoint(point);

  features.forEach(feature => {
    const vertices = explode(feature);
    const nearest = nearestPoint(targetPoint, vertices);
    const d = distance(targetPoint, nearest);

    if (d < thresholdKm && d < minDistance) {
      minDistance = d;
      nearestVertex = nearest;
    }
  });

  return nearestVertex ? nearestVertex.geometry.coordinates : point;
};

/**
 * Snaps a point to the nearest point on a polygon boundary.
 * Useful for real-time snapping during drawing.
 * @param {Array} point - [lng, lat]
 * @param {Object} polygonGeometry - GeoJSON Polygon geometry
 * @returns {Array} Snapped point [lng, lat]
 */
export const snapPointToPolygonBoundary = (point, polygonGeometry) => {
    if (!polygonGeometry) return point;
    
    try {
        const pt = turfPoint(point);
        
        // If point is inside, don't snap
        if (booleanPointInPolygon(pt, polygonGeometry)) {
            return point;
        }

        // If outside, snap to nearest point on exterior ring
        const line = polygonToLine(polygonGeometry);
        const snapped = nearestPointOnLine(line, pt);
        return snapped.geometry.coordinates;
    } catch (err) {
        console.error("Error in snapPointToPolygonBoundary:", err);
        return point;
    }
};
/**
 * Snaps a point to the nearest point on any of the provided polygon boundaries.
 * @param {Array} point - [lng, lat]
 * @param {Array} polygons - Array of GeoJSON Polygon features
 * @param {Object} farmBoundary - Optional farm boundary to also snap to (must stay inside)
 * @returns {Array} Snapped point [lng, lat]
 */
export const snapPointToMultiplePolygons = (point, polygons, farmBoundary = null) => {
    let snappedPoint = point;
    const pt = turfPoint(point);

    // 1. Ensure we stay INSIDE the farm boundary
    if (farmBoundary && farmBoundary.geometry) {
        snappedPoint = snapPointToPolygonBoundary(snappedPoint, farmBoundary.geometry);
    }

    // 2. Ensure we stay OUTSIDE (or on boundary) of existing forest stands
    // If the point is inside any existing stand, snap it to the boundary of that stand
    let minDistance = Infinity;
    let bestSnap = snappedPoint;
    let isInsideAny = false;

    polygons.forEach(stand => {
        if (stand.geometry && booleanPointInPolygon(pt, stand.geometry)) {
            isInsideAny = true;
            try {
                const line = polygonToLine(stand.geometry);
                const snapped = nearestPointOnLine(line, pt);
                const d = distance(pt, snapped);
                if (d < minDistance) {
                    minDistance = d;
                    bestSnap = snapped.geometry.coordinates;
                }
            } catch (err) {
                console.error("Error snapping to stand boundary:", err);
            }
        }
    });

    if (isInsideAny) {
        return bestSnap;
    }

    return snappedPoint;
};
